import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, Check, FileSignature, PartyPopper, Archive, CalendarClock, Globe } from "lucide-react";
import { fmtD } from "../lib/date";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STEPS = [
  { k: "new", zh: "已收到简历", en: "Application Received" },
  { k: "screening", zh: "简历筛选", en: "Resume Screening" },
  { k: "interview", zh: "面试中", en: "Interviewing" },
  { k: "offer", zh: "Offer 阶段", en: "Offer Stage" },
  { k: "hired", zh: "已录用", en: "Hired" },
];
const MODES = { onsite: { zh: "现场", en: "On-site" }, video: { zh: "视频", en: "Video" }, phone: { zh: "电话", en: "Phone" } };
const T = {
  zh: {
    brand: "ALIGCUS 阿里建筑 · 应聘进度查询", sub: "Application Status", invalid: "查询链接无效，请核对后重试",
    loading: "查询中…", cand: "候选人", applyFor: "应聘", submitted: "投递日期", updated: "最近更新",
    current: "（当前阶段）", rejTitle: "感谢您的投递，当前职位暂不匹配",
    rejSub: "您的简历已纳入我们的人才库，如有合适职位将第一时间与您联系。",
    congrats: "恭喜！欢迎加入 ALIGCUS",
    ivTitle: (r) => `面试安排（第 ${r} 轮）`, ivMode: "面试", ivLoc: "地点：",
    ivNote: "具体地址/会议链接以 HR 与您的电话或邮件确认为准，请提前 10 分钟到场/上线",
    esign: "您有一份 Offer 待签署，点击在线签署", esigned: "✍️ Offer 与入职文件已签署完成", back: "← 返回招聘门户",
  },
  en: {
    brand: "ALIGCUS Construction · Application Status", sub: "Track your application", invalid: "Invalid tracking link, please check and retry",
    loading: "Loading…", cand: "Candidate", applyFor: "applying for", submitted: "Submitted", updated: "Last update",
    current: " (current)", rejTitle: "Thank you for applying — not a match for this position",
    rejSub: "Your resume has been added to our talent pool. We will reach out when a suitable role opens.",
    congrats: "Congratulations! Welcome to ALIGCUS",
    ivTitle: (r) => `Interview Scheduled (Round ${r})`, ivMode: " interview", ivLoc: "Location: ",
    ivNote: "HR will confirm the exact address / meeting link by phone or email. Please arrive 10 minutes early.",
    esign: "You have an Offer waiting for signature — sign online", esigned: "✍️ Offer & onboarding documents signed", back: "← Back to careers",
  },
};

export default function CareersStatus() {
  const { token } = useParams();
  const [lang, setLang] = useState(localStorage.getItem("careers_lang") || "zh");
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const t = T[lang];

  const toggleLang = () => {
    const nl = lang === "zh" ? "en" : "zh";
    setLang(nl);
    localStorage.setItem("careers_lang", nl);
  };

  useEffect(() => {
    fetch(`${BACKEND}/api/careers/status/${token}`)
      .then((r) => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setData)
      .catch(() => setErr(true));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-slate-500 p-6 text-center" data-testid="careers-status-error">{t.invalid}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin mr-2" />{t.loading}</div>;

  const idx = STEPS.findIndex((s) => s.k === data.stage);
  const rejected = data.stage === "rejected";
  const ivMode = data.interview ? (MODES[data.interview.modeKey]?.[lang] || data.interview.mode) : "";

  return (
    <div className="min-h-screen bg-slate-100" data-testid="careers-status-page">
      <div className="bg-slate-900 text-white px-5 py-4 flex items-center gap-2">
        <div>
          <p className="font-bold">{t.brand}</p>
          <p className="text-[11px] text-slate-300">{t.sub}</p>
        </div>
        <button data-testid="careers-status-lang-toggle" onClick={toggleLang}
          className="ml-auto inline-flex items-center gap-1 rounded-full border border-slate-600 text-slate-200 hover:border-slate-400 text-xs font-semibold px-3 py-1.5">
          <Globe className="h-3 w-3" />{lang === "zh" ? "EN" : "中文"}
        </button>
      </div>
      <div className="max-w-[600px] mx-auto p-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 my-4">
          <p className="text-sm text-slate-500">{t.cand} <b className="text-slate-800">{data.name}</b> · {t.applyFor} <span className="text-red-600 font-semibold">{data.jobTitle}</span></p>
          <p className="text-[11px] text-slate-400 mt-0.5">{t.submitted} {data.submittedAt} · {t.updated} {data.updatedAt}</p>

          {rejected ? (
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-200 p-5 text-center" data-testid="careers-status-rejected">
              <Archive className="h-8 w-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm font-semibold text-slate-600">{t.rejTitle}</p>
              <p className="text-[12px] text-slate-400 mt-1">{t.rejSub}</p>
            </div>
          ) : (
            <div className="mt-5 space-y-0" data-testid="careers-status-timeline">
              {STEPS.map((s, i) => (
                <div key={s.k} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div className={`h-7 w-7 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0
                      ${i < idx ? "bg-emerald-500 text-white" : i === idx ? "bg-red-600 text-white animate-pulse" : "bg-slate-100 text-slate-400"}`}>
                      {i < idx ? <Check className="h-3.5 w-3.5" /> : i + 1}
                    </div>
                    {i < STEPS.length - 1 && <div className={`w-0.5 flex-1 min-h-[24px] ${i < idx ? "bg-emerald-300" : "bg-slate-100"}`} />}
                  </div>
                  <div className="pb-5">
                    <p className={`text-sm font-semibold ${i === idx ? "text-red-600" : i < idx ? "text-slate-700" : "text-slate-400"}`}
                      data-testid={i === idx ? "careers-status-stage" : undefined}>{s[lang]}{i === idx ? t.current : ""}</p>
                    {s.k === "hired" && i === idx && <p className="text-[12px] text-emerald-600 mt-0.5 flex items-center gap-1"><PartyPopper className="h-3.5 w-3.5" />{t.congrats}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}

          {data.interview && (
            <div className="mt-2 rounded-xl bg-indigo-50 border border-indigo-200 p-4" data-testid="careers-status-interview">
              <p className="text-sm font-bold text-indigo-800 flex items-center gap-1.5"><CalendarClock className="h-4 w-4" />{t.ivTitle(data.interview.round)}</p>
              <p className="text-sm text-slate-700 mt-1">{fmtD(data.interview.date)} {data.interview.time} · {ivMode}{t.ivMode}{data.interview.modeKey === "onsite" ? ` · ${t.ivLoc}${data.branch}` : ""}</p>
              <p className="text-[11px] text-slate-400 mt-1">{t.ivNote}</p>
            </div>
          )}

          {data.esignToken && (
            <Link data-testid="careers-status-esign-link" to={`/esign/offer/${data.esignToken}`}
              className="mt-2 w-full h-12 rounded-xl bg-brand-grad hover:opacity-90 text-white font-bold flex items-center justify-center gap-2">
              <FileSignature className="h-4 w-4" />{t.esign}
            </Link>
          )}
          {data.esignSigned && (
            <p data-testid="careers-status-esign-signed" className="mt-2 text-[12px] text-emerald-600 font-semibold text-center">{t.esigned}</p>
          )}
        </div>
        <p className="text-[11px] text-slate-400 text-center"><Link to="/careers" className="underline">{t.back}</Link></p>
      </div>
    </div>
  );
}
